/**
 * lib/commentProofPdf.ts
 * ------------------------------------------------------------------
 * Editor's proof sheet (13.6) — a printable, per-student dump of the
 * FULL comment/mark picture for a class so any reviewer can proof-read
 * and sign off before reports are generated. For every student it
 * lists EVERY subject they take (even ones with no mark or comment yet),
 * with the term CA mark, the exam mark, the teacher who entered the
 * comment, and the comment itself — followed by the class-teacher and
 * Head of Year comments. Reflects the comments currently in the review
 * workspace (including unsaved edits), which is what the reviewer checks.
 *
 * jspdf-autotable drives the grid so it paginates with a repeating
 * header; the shared LKC letterhead / footer chrome is drawn per page.
 *
 *   buildCommentProofPdf(data).save('CommentProof-Form3A-Term1.pdf')
 * ------------------------------------------------------------------
 */
import { jsPDF } from 'jspdf';
import autoTable, {
  type CellHookData,
  type RowInput,
  type Styles,
} from 'jspdf-autotable';
import {
  PDF,
  CONTENT_TOP,
  pageGeom,
  drawLetterhead,
  stampFooters,
} from '@/lib/pdfChrome';

export interface CommentProofSubject {
  subject: string;
  ca: string;
  exam: string;
  teacher: string;
  comment: string;
}

export interface CommentProofStudent {
  name: string;
  subjects: CommentProofSubject[];
  classTeacher: string;
  headOfYear: string;
}

export interface CommentProofPdfData {
  className: string;
  metaLine?: string;
  students: CommentProofStudent[];
  logoDataUrl?: string | null;
  generatedBy?: string;
}

type ProofRowMeta = 'band' | 'subject' | 'remark';

export function buildCommentProofPdf(data: CommentProofPdfData): jsPDF {
  const geom = pageGeom('p');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  const head = [['Subject', 'CA', 'Exam', 'Teacher', 'Comment']];
  const nCols = 5;

  const body: RowInput[] = [];
  const meta: ProofRowMeta[] = [];

  const bandCell = (text: string): RowInput => [
    {
      content: text,
      colSpan: nCols,
      styles: {
        fillColor: PDF.BAND,
        textColor: PDF.NAVY,
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'left',
        cellPadding: { top: 2, right: 3, bottom: 2, left: 3 },
      },
    },
  ];
  const remarkCell = (label: string, text: string): RowInput => [
    {
      content: `${label}:  ${(text || '').trim() || '—'}`,
      colSpan: nCols,
      styles: {
        textColor: (text || '').trim() ? PDF.INK : PDF.MUTED,
        fontStyle: 'italic',
        fontSize: 7.5,
        halign: 'left',
        cellPadding: { top: 1.4, right: 3, bottom: 1.4, left: 5 },
      },
    },
  ];

  for (const stu of data.students) {
    body.push(bandCell(stu.name));
    meta.push('band');
    if (stu.subjects.length === 0) {
      body.push([{ content: '— no subjects on record —', colSpan: nCols, styles: { textColor: PDF.MUTED, fontStyle: 'italic', halign: 'left' } }]);
      meta.push('subject');
    } else {
      for (const s of stu.subjects) {
        body.push([s.subject, s.ca || '–', s.exam || '–', s.teacher || '—', s.comment || '—']);
        meta.push('subject');
      }
    }
    body.push(remarkCell("Class Teacher's comment", stu.classTeacher));
    meta.push('remark');
    body.push(remarkCell("Head of Year's comment", stu.headOfYear));
    meta.push('remark');
  }

  const columnStyles: Record<number, Partial<Styles>> = {
    0: { cellWidth: 30, halign: 'left', fontStyle: 'bold' },
    1: { cellWidth: 11, halign: 'center' },
    2: { cellWidth: 12, halign: 'center' },
    3: { cellWidth: 32, halign: 'left', textColor: PDF.MUTED },
    4: { cellWidth: 'auto', halign: 'left' },
  };

  autoTable(pdf, {
    head,
    body,
    startY: CONTENT_TOP,
    margin: { top: CONTENT_TOP, left: geom.MX, right: geom.MX, bottom: 14 },
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 7.5,
      textColor: PDF.INK,
      cellPadding: { top: 1.4, right: 2, bottom: 1.4, left: 2 },
      lineColor: PDF.RULE,
      lineWidth: { bottom: 0.1, top: 0, left: 0, right: 0 },
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: PDF.NAVY,
      textColor: PDF.WHITE,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'center',
      lineWidth: 0,
    },
    columnStyles,
    // Keep a student's band row from being orphaned at the foot of a page.
    rowPageBreak: 'avoid',
    didDrawPage: () => {
      drawLetterhead(pdf, {
        title: 'COMMENT PROOF',
        metaLine: data.metaLine ?? data.className,
        logoDataUrl: null, // ink-saver working document — no logo
        geom,
      });
    },
    didParseCell: (h: CellHookData) => {
      if (h.section !== 'body') return;
      const m = meta[h.row.index];
      if (m === 'subject' && h.column.index >= 1 && h.column.index <= 2) {
        // Blank marks stay muted so entered marks stand out.
        const raw = Array.isArray(h.cell.text) ? h.cell.text[0] : h.cell.text;
        if (raw === '–' || raw === '') h.cell.styles.textColor = PDF.MUTED;
      }
    },
  });

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}
